//WorkFlowList.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { WorkFlowType } from '../data/WorkFlowType';
import { getWorkFlows } from '../../../service/EditWorkFlowService';
import { axiosRequest } from '../../../service/AxiosService';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Checkbox,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  Pagination,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Typography,
  Snackbar,
} from '@mui/material';
import { ExpandMore as ChevronDown, ExpandLess as ChevronUp } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import { Search as SearchIcon } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import { InputAdornment } from '@mui/material';
import theme from '../../../style/theme';
import ClearSharpIcon from '@mui/icons-material/ClearSharp';

// テーブルのスタイルを設定
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.custom.gray,
    color: theme.palette.common.white,
    whiteSpace: 'nowrap', // 改行しない
    padding: '6px 20px',
  },
  [`&.${tableCellClasses.body}`]: {
    whiteSpace: 'nowrap',
    padding: '6px 20px',
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));


// テーブルに表示するカラムキーと表示名のマッピング
const columnLabels: Partial<Record<keyof WorkFlowType, string>> = {
  code: '番号',
  version: 'バージョン',
  type: '申請種別',
  name: '申請書',
  description: '詳細',
};

/**
 * ワークフロー一覧コンポーネント
 * - ワークフローの検索・フィルタリング・ソート・ページング・削除・復元機能を提供する
 */
const WorkFlowList: React.FC = () => {
  const isMobile = useMediaQuery('(max-width:600px)');  // 600px 以下でモバイル表示
  const [data, setData] = useState<WorkFlowType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(() => {
    return sessionStorage.getItem('searchText_EditWorkFlow') || '';// sessionStorageに値がある場合はその値を格納。ない場合は空文字を格納。
  });
  const [sortConfig, setSortConfig] = useState<{ key: keyof WorkFlowType; asc: boolean }>({
    key: 'code',
    asc: false,
  });
  const [hoveredColumn, setHoveredColumn] = useState<string | null>(null);
  // sessionStorageに値がある場合はその値を格納。ない場合はデフォルト値を格納。
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => {
    const stored = sessionStorage.getItem("visibleColomns_EditWorkFlow");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          return new Set<string>(parsed);
        }
      } catch (e) {
        console.warn("visibleColumns の復元に失敗しました:", e);
      }
    }
    return new Set<string>(Object.keys(columnLabels));
  });
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  // 表示件数のデフォルトと選択できる件数を設定。
  const defaultRowPerPage = 50;
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    return Number(sessionStorage.getItem("showItems_EditWorkFlow")) || defaultRowPerPage;// sessionStorageに値がある場合はその値を格納。ない場合はデフォルト値を格納。
  });
  const rowPerPageList = [50, 100, 1000];
  const [page, setPage] = useState(() => {
    // sessionStorageに値がある場合はその値を格納。それ以外は1をセットする。
    const stored = sessionStorage.getItem("paging_EditWorkflow");
    const parsed = stored ? parseInt(stored, 10) : NaN;
    return !isNaN(parsed) && parsed > 0 ? parsed : 1;
  });
  const [showSearchSettings, setShowSearchSettings] = useState(false);
  const [columnSearchTerms, setColumnSearchTerms] = useState<Record<string, string>>({});
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set()); // 選ばれたデータ記録
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [restoreTargetId, setRestoreTargetId] = useState<string | null>(null);
  const [showDeleted, setShowDeleted] = useState(() => {
    const stored = sessionStorage.getItem("showDeleted_EditWorkFlow");
    return stored === "true";
  }); // 削除済みワークフローを表示するかどうか
  const [showDraft, setShowDraft] = useState(() => {
    const stored = sessionStorage.getItem("showDraft_EditWorkFlow");
    return stored !== "false"; // デフォルトはtrue（表示）
  }); // 一時保存中のワークフローを表示するかどうか
  const [hoveredDeletedRow, setHoveredDeletedRow] = useState<string | null>(null); // ホバーされている削除済み行のID
  const navigate = useNavigate();
  const [showSnackbar, setShowSnackbar] = useState(false); // Snackbarの表示状態を管理
  const [snackbarMessage, setSnackbarMessage] = useState("ワークフローの登録／更新が完了しました。"); // Snackbarに表示するメッセージ

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const result = await getWorkFlows();
      setData(result);
      setLoading(false);
    };
    loadData();
    // 検索設定に関してsessionStorageに値がある場合はその値を反映する。
    const savedTerms = sessionStorage.getItem('columnSearchTerms');
    if (savedTerms) {
      try {
        const parsedTerms = JSON.parse(savedTerms);
        if (typeof parsedTerms === 'object' && parsedTerms !== null) {
          const sanitizedTerms = Object.fromEntries(
            Object.entries(parsedTerms).filter(
              ([_, v]) => typeof v === 'string' && v !== ''
            )
          ) as Record<string, string>;
          setColumnSearchTerms(sanitizedTerms);
        }
      } catch (e) {
        console.warn('Invalid sessionStorage value for columnSearchTerms');
      }
    }
  }, []);

  // 表示カラムの切り替え（チェックボックス操作時に呼び出す）
  const toggleColumn = (key: string) => {
    const newSet = new Set(visibleColumns);
    newSet.has(key) ? newSet.delete(key) : newSet.add(key);
    sessionStorage.setItem("visibleColomns_EditWorkFlow", JSON.stringify(Array.from(newSet)));// 表示するカラム情報をsessionStorageに格納する。
    setVisibleColumns(newSet);
  };

  const filteredData = useMemo(() => {

    let result = [...data];

    // 削除済みワークフローのフィルタリング
    if (!showDeleted) {
      result = result.filter((row) => !row.isDeleted);
    }

    // 一時保存中ワークフローのフィルタリング
    if (!showDraft) {
      result = result.filter((row) => !row.isDraft);
    }

    // 全体検索キーワード（検索欄）
    const keywords = searchTerm.toLowerCase().split(/\s+/).filter(Boolean);
    if (keywords.length > 0) {
      result = result.filter((row) =>
        keywords.every((keyword) =>
          Object.values(row).some((value) =>
            typeof value === 'string' && value.toLowerCase().includes(keyword)
          )
        )
      );
    }

    // 項目別検索
    const activeColumnSearches = Object.entries(columnSearchTerms).filter(([_, value]) => value.trim() !== '');
    if (activeColumnSearches.length > 0) {
      result = result.filter((row) =>
        activeColumnSearches.every(([key, value]) => {
          const cellValue = row[key as keyof WorkFlowType];
          return typeof cellValue === 'string' && cellValue.toLowerCase().includes(value.toLowerCase());
        })
      );
    }

    // ソート処理
    const { key, asc } = sortConfig;
    result.sort((a, b) => {
      const aValue = a[key];
      const bValue = b[key];
      return asc
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });

    setPage(1);
    return result;
  }, [searchTerm, columnSearchTerms, data, sortConfig, showDeleted, showDraft]);

  // 削除済み行がselectedRowsに含まれている場合は除去
  useEffect(() => {
    const deletedIds = data.filter(row => row.isDeleted).map(row => row.applicationId);
    const hasDeletedSelected = Array.from(selectedRows).some(id => deletedIds.includes(id));

    if (hasDeletedSelected) {
      const newSelectedRows = new Set(Array.from(selectedRows).filter(id => !deletedIds.includes(id)));
      setSelectedRows(newSelectedRows);
    }
  }, [data, selectedRows]);

  // 現在のページに表示するデータを絞り込む
  const paginatedData = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  // 項目別検索フィールドの入力値を更新し、sessionStorageに反映する
  const handleColumnSearchChange = (key: string, value: string) => {
    const updated = {
      ...columnSearchTerms,
      [key]: value,
    };
    setColumnSearchTerms(updated);
    // すべての値が空文字なら sessionStorage を削除
    const allEmpty = Object.values(updated).every(v => v === '');
    if (allEmpty) {
      sessionStorage.removeItem('columnSearchTerms');
    } else {
      sessionStorage.setItem('columnSearchTerms', JSON.stringify(updated));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-40">
        <CircularProgress />
      </div>
    );
  }

  // フィルターの条件を初期化する
  const filterReset = () => {
    // sessionStorageを削除する
    sessionStorage.removeItem("searchText_EditWorkFlow");
    sessionStorage.removeItem("showItems_EditWorkFlow");
    sessionStorage.removeItem("visibleColomns_EditWorkFlow");
    sessionStorage.removeItem("columnSearchTerms");
    sessionStorage.removeItem("paging_EditWorkflow");
    sessionStorage.removeItem("showDeleted_EditWorkFlow");
    sessionStorage.removeItem("showDraft_EditWorkFlow");

    // フィルター条件の初期化
    setSearchTerm('');
    setVisibleColumns(new Set(Object.keys(columnLabels)));
    setRowsPerPage(defaultRowPerPage);
    setColumnSearchTerms({});
    setPage(1);
    setShowDeleted(false);
    setShowDraft(true);
  };

  // テキストボックスのテキストをクリア
  const handleFilterTextClear = () => {
    // フィルター検索のテキストを初期化
    setSearchTerm("");
    // sessionStorageの値をクリアする
    sessionStorage.removeItem("searchText_EditWorkFlow");
  };

  return (
    <div className="p-4">
      <Box display="flex" alignItems="center" gap={2}>
        {/* 検索欄 */}
        <div className="mb-2 flex items-center gap-2">
          <TextField
            label="検索"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => {
              const value = e.target.value;
              setSearchTerm(value);
              // 空の場合はsessionStorageを削除。それ以外は値を格納する。
              if (value === "") {
                sessionStorage.removeItem("searchText_EditWorkFlow");
              } else {
                sessionStorage.setItem("searchText_EditWorkFlow", value);
              }
            }}
            className="w-64"
            sx={{ ml: 1, mt: 2, width: 220 }}
            slotProps={{
              input: {
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleFilterTextClear}
                      edge="end"
                      sx={{
                        backgroundColor: 'transparent', // 背景透明
                        '&:hover': {
                          backgroundColor: 'transparent', // ホバー時も透明
                        }
                      }}>
                      <ClearSharpIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }
            }}
          />
        </div>
        {/* フィルターのリセット機能(初期化して、sessionStorageをclearにする) */}
        <Chip
          label='リセット'
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
            ml: 1, mt: 2
          }}
          onClick={filterReset}
        />
      </Box>


      {/* 表示設定 */}
      <Button
        sx={{ ml: 1, mt: 1, typography: 'buttonMedium' }}
        onClick={() => setShowColumnSettings(!showColumnSettings)}
        startIcon={showColumnSettings ? <ChevronUp /> : <ChevronDown />}
      >
        表示設定
      </Button>

      <Box sx={{ pl: 2, pb: 1 }}>
        {showColumnSettings && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>

            {/* 表示件数 */}
            <Box sx={{ mb: 0 }}>
              <div style={{ fontWeight: 'bold', color: 'grey', marginBottom: -6 }}>表示件数:</div>
              <RadioGroup
                row
                value={rowsPerPage.toString()}
                onChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value));
                  sessionStorage.setItem("showItems_EditWorkFlow", e.target.value);// 表示設定が変わったら、sessionStorageに値を格納する。
                  setPage(1);
                  sessionStorage.removeItem("paging_EditWorkflow");// 表示設定が変わったら、sessionStorageの値を削除する。
                }}
              >
                {rowPerPageList.map((num) => (
                  <FormControlLabel
                    key={num}
                    value={num.toString()}
                    control={<Radio sx={{ transform: 'scale(0.6)' }} />}
                    label={`${num}件`}
                  />
                ))}
              </RadioGroup>
            </Box>

            {/* 表示項目 */}
            <Box>
              <div className="font-semibold mb-2" style={{ fontWeight: 'bold', color: 'grey', marginBottom: 4 }}>表示項目:</div>
              <Box className="flex flex-wrap gap-2">
                {Object.entries(columnLabels).map(([key, label]) => (
                  <label key={key} className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={visibleColumns.has(key)}
                      onChange={() => toggleColumn(key)}
                    />
                    {label}
                  </label>
                ))}
                <label className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={showDeleted}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setShowDeleted(checked);
                      sessionStorage.setItem("showDeleted_EditWorkFlow", checked.toString());
                    }}
                  />
                  削除済
                </label>
                <label className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={showDraft}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setShowDraft(checked);
                      sessionStorage.setItem("showDraft_EditWorkFlow", checked.toString());
                    }}
                  />
                  一時保存中
                </label>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* 検索設定 */}
      <Button
        sx={{ ml: 1, mt: -1, typography: 'buttonMedium' }}
        onClick={() => setShowSearchSettings(!showSearchSettings)}
        startIcon={showSearchSettings ? <ChevronUp /> : <ChevronDown />}
      >
        検索設定
      </Button>

      <Box sx={{ pl: 2, pb: 2 }}>
        {showSearchSettings && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {Object.entries(columnLabels)
              .filter(([key]) => key !== 'actions')
              .map(([key, label]) => (
                <Box key={key} sx={{ display: 'flex', flexDirection: 'column', minWidth: 100 }}>
                  <div style={{ fontSize: '1rem', marginBottom: 4 }}>{label}</div>
                  <TextField
                    size="small"
                    variant="outlined"
                    value={columnSearchTerms[key] || ''}
                    onChange={(e) => handleColumnSearchChange(key, e.target.value)}
                    sx={{
                      width: 140,
                      '& input': {
                        padding: '4px 8px',
                      },
                    }}
                    InputProps={{
                      startAdornment: columnSearchTerms[key]
                        ? undefined
                        : (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                    }}
                  />
                </Box>
              ))}
          </Box>
        )}
      </Box>

      {/* 新規作成ボタン */}
      <Box sx={{ mt: 0, mb: 0, display: 'flex', gap: 2 }}>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} sx={{ ml: 1 }} onClick={() => navigate("/edit/new")}>
          ワークフロー新規作成
        </Button>
        {/* 削除ボタン */}
        {selectedRows.size > 0 && ( //データが選ばれた際に表示される
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setShowDeleteDialog(true)} //クリックすると確認ダイアログが出る
          >
            削除
          </Button>
        )}

      </Box>
      {/* ページング */}
      <Box sx={{ ml: 2, my: 1, display: 'flex', gap: 2, justifyContent: 'flex-start', alignItems: 'center' }}>
        {/* 件数表示 */}
        <div style={{ marginBottom: 1, color: '#666', fontSize: '1rem' }}>
          全 {filteredData.length} 件中{' '}
          {filteredData.length === 0
            ? '0 件 〜 0 件'
            : `${(page - 1) * rowsPerPage + 1} 件 〜 ${Math.min(page * rowsPerPage, filteredData.length)
            } 件`} を表示
        </div>
        <div >
          <Pagination
            count={Math.ceil(filteredData.length / rowsPerPage)}
            page={page}
            onChange={(e, value) => {
              setPage(value);
              sessionStorage.setItem("paging_EditWorkflow", value.toString());// ページが変わったら、現在のページをsessionStorageに格納する。
            }}
            color="primary"
            sx={{
              '& .MuiPaginationItem-root': {
                minWidth: '20px',
                height: '20px',
                padding: '1px',
              },
            }}
          />
        </div>
      </Box>

      {/* テーブル */}
      <TableContainer component={Paper}
        sx={{
          ml: isMobile ? 0 : 1,
          mb: 2,
          overflow: "auto", // スクロールを表示
          maxHeight: "calc(100vh - 370px)",
          maxWidth: '100%',
        }}>
        <Table 
          sx={{ tableLayout: 'auto', minWidth: 800 }} 
          aria-label="customized table"
          stickyHeader
        >
          <TableHead>
            <TableRow>
              <StyledTableCell>
                <Tooltip title={selectedRows.size === filteredData.filter(row => !row.isDeleted).length && filteredData.filter(row => !row.isDeleted).length > 0 ? 'すべて選択解除' : 'すべて選択'}>
                  <Checkbox
                    checked={selectedRows.size === filteredData.filter(row => !row.isDeleted).length && filteredData.filter(row => !row.isDeleted).length > 0}
                    onChange={() => {
                      const selectableRows = filteredData.filter(row => !row.isDeleted);
                      if (selectedRows.size === selectableRows.length && selectableRows.length > 0) {
                        setSelectedRows(new Set());
                      } else {
                        setSelectedRows(new Set(selectableRows.map((row) => row.applicationId)));
                      }
                    }}
                  />
                </Tooltip>
              </StyledTableCell>
              <StyledTableCell sx={{ width: 'auto', minWidth: '100px' }}>
                保存状態
              </StyledTableCell>

              {Object.entries(columnLabels).map(([key, label]) => {
                if (!visibleColumns.has(key)) return null;
                const isActive = sortConfig?.key === key;
                const asc = sortConfig?.asc;

                return (

                  <StyledTableCell
                    key={key}
                    onClick={() => {
                      setPage(1); // 第一ページに戻る
                      setSortConfig((prev) =>
                        prev?.key === key
                          ? { key: key as keyof WorkFlowType, asc: !prev.asc }
                          : { key: key as keyof WorkFlowType, asc: true }
                      );
                    }}
                    onMouseEnter={() => setHoveredColumn(key)}
                    onMouseLeave={() => setHoveredColumn(null)}
                    style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    <div style={{ display: 'inline-flex', alignItems: 'center', }} >
                      {label}
                      {(isActive || hoveredColumn === key) && (
                        asc && isActive ? (
                          <ChevronUp fontSize="small" className="ml-1 text-gray-500" />
                        ) : !asc && isActive ? (
                          <ChevronDown fontSize="small" className="ml-1 text-gray-500" />
                        ) : (
                          <ChevronDown fontSize="small" className="ml-1 text-gray-300" />
                        )
                      )}
                    </div>

                  </StyledTableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedData.map((row) => (
              <StyledTableRow
                key={row.applicationId}
                onClick={(e) => {
                  // checkboxをクリックするときは遷移しない
                  if ((e.target as HTMLElement).closest('input[type="checkbox"]')) return;
                  // WF編集画面へ遷移する（ /edit/:id）
                  navigate(`/edit/${row.applicationId}`);
                }}
                sx={{
                  height: '48px',
                  cursor: 'pointer',
                  backgroundColor: row.isDeleted ? '#f5f5f5' : 'inherit', // 削除済みの行はグレー背景
                  opacity: row.isDeleted ? 0.6 : 1, 
                  filter: row.isDeleted ? 'grayscale(30%)' : 'none', 
                  '&:hover': {
                    backgroundColor: '#e3f2fd', // 浅いブルー
                    boxShadow: 'inset 0 0 0 1px #90caf9',
                  },
                }}
              >
                <StyledTableCell sx={{}}>

                  {row.isDeleted ? (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setRestoreTargetId(row.applicationId);
                        setShowRestoreDialog(true);
                      }}
                      sx={{
                        minWidth: '60px',
                        padding: '2px 8px',
                        fontSize: '0.875rem',
                        opacity: 1,
                      }}
                    >
                      復元
                    </Button>
                  ) : (
                    <Checkbox
                      checked={selectedRows.has(row.applicationId)}
                      onChange={(e) => {
                        e.stopPropagation(); // クリックイベントを防止
                        const newSet = new Set(selectedRows);
                        if (newSet.has(row.applicationId)) {
                          newSet.delete(row.applicationId);
                        } else {
                          newSet.add(row.applicationId);
                        }
                        setSelectedRows(newSet);
                      }}
                    />
                  )}
                </StyledTableCell>
                <StyledTableCell sx={{ width: 'auto', minWidth: '100px' }}>
                  {row.isDeleted ? (
                    <Chip
                      label="削除済"
                      size="small"
                      sx={{
                        backgroundColor: '#757575',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.75rem',
                        height: '25px',
                        '& .MuiChip-label': {
                          paddingLeft: '10px',
                          paddingRight: '10px',
                        },
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      }}
                    />
                  ) : row.isDraft ? (
                    <Chip
                      label="一時保存"
                      size="small"
                      sx={{
                        backgroundColor: 'white',
                        color: theme.palette.primary.main,
                        fontWeight: 'bold',
                        fontSize: '0.75rem',
                        height: '25px',
                        '& .MuiChip-label': {
                          paddingLeft: '10px',
                          paddingRight: '10px',
                        },
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        border: `1px solid ${theme.palette.primary.main}`,
                      }}
                    />
                  ) : (
                    <Chip
                      label="保存済"
                      size="small"
                      sx={{
                        backgroundColor: theme.palette.primary.main,
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.75rem',
                        height: '25px',
                        '& .MuiChip-label': {
                          paddingLeft: '10px',
                          paddingRight: '10px',
                        },
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      }}
                    />
                  )}
                </StyledTableCell>

                {Object.keys(columnLabels)
                  .filter((key) => visibleColumns.has(key))
                  .map((key) => (
                    <StyledTableCell key={key} align="left" title="編集">
                      {row[key as keyof WorkFlowType]}
                    </StyledTableCell>
                  ))}
              </StyledTableRow>
            ))}
          </TableBody>

        </Table>
      </TableContainer>
      {/* ページング */}
      <Box sx={{ ml: 2, mb: 1, display: 'flex', gap: 2, justifyContent: 'flex-start', alignItems: 'center' }}>
        {/* 件数表示 */}
        <div style={{ marginTop: 1, color: '#666', fontSize: '1rem' }}>
          全 {filteredData.length} 件中{' '}
          {filteredData.length === 0
            ? '0 件 〜 0 件'
            : `${(page - 1) * rowsPerPage + 1} 件 〜 ${Math.min(page * rowsPerPage, filteredData.length)
            } 件`} を表示
        </div>
        <div >
          <Pagination
            count={Math.ceil(filteredData.length / rowsPerPage)}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
            sx={{
              '& .MuiPaginationItem-root': {
                minWidth: '20px',
                height: '20px',
                padding: '1px',
              },
            }}
          />
        </div>
        <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
          <DialogTitle>削除確認</DialogTitle>
          <DialogContent>
            <DialogContentText>
              選択された {selectedRows.size} 件のワークフローを削除しますか？
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDeleteDialog(false)}>キャンセル</Button>
            <Button
              onClick={async () => {
                try {
                  // 選択されたapplicationIdのリストを取得
                  const applicationIds = Array.from(selectedRows);

                  // 後端APIを呼び出してワークフローを削除
                  await axiosRequest(
                    "/workFlows/delete",
                    "POST",
                    {
                      data: {
                        applicationIds
                      }
                    }
                  );

                  // 削除成功後、フロントエンドの状態を更新（isDeletedをtrueに設定）
                  setData((prev) =>
                    prev.map((item) =>
                      selectedRows.has(item.applicationId)
                        ? { ...item, isDeleted: true }
                        : item
                    )
                  );
                  setSelectedRows(new Set());
                  setShowDeleteDialog(false);
                  setSnackbarMessage("ワークフローの削除が完了しました。");
                  setShowSnackbar(true);
                } catch (error) {
                  console.error("ワークフローの削除に失敗しました:", error);
                  // エラーが発生した場合もダイアログを閉じる（必要に応じてエラーメッセージを表示）
                  setShowDeleteDialog(false);
                }
              }}
              color="error"
            >
              削除
            </Button>
          </DialogActions>
        </Dialog>

        {/* 復元確認ダイアログ */}
        <Dialog open={showRestoreDialog} onClose={() => setShowRestoreDialog(false)}>
          <DialogTitle>復元確認</DialogTitle>
          <DialogContent>
            <DialogContentText>
              選択されたワークフローを復元しますか？
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowRestoreDialog(false)}>キャンセル</Button>
            <Button
              onClick={async () => {
                if (!restoreTargetId) return;

                try {
                  // 復元API呼び出し
                  await axiosRequest(
                    "/workFlows/restore",
                    "POST",
                    {
                      data: {
                        applicationId: restoreTargetId
                      }
                    }
                  );

                  // 復元成功後、フロントエンドの状態を更新
                  setData((prev) =>
                    prev.map((item) =>
                      item.applicationId === restoreTargetId
                        ? { ...item, isDeleted: false }
                        : item
                    )
                  );

                  setShowRestoreDialog(false);
                  setRestoreTargetId(null);
                  setSnackbarMessage("ワークフローの復元が完了しました。");
                  setShowSnackbar(true);
                } catch (error) {
                  console.error("ワークフローの復元に失敗しました:", error);
                  setShowRestoreDialog(false);
                  setRestoreTargetId(null);
                }
              }}
              color="primary"
            >
              復元
            </Button>
          </DialogActions>
        </Dialog>

        {/* 登録/更新完了メッセージ */}
        <Snackbar
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          open={showSnackbar}
          onClose={() => setShowSnackbar(false)}
          message={snackbarMessage}
          autoHideDuration={6000}
          action={
            <IconButton
              size="small"
              aria-label="closeSnackbar"
              color="inherit"
              onClick={() => setShowSnackbar(false)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        />

      </Box>
    </div >


  );
};

export default WorkFlowList;